import React, { useMemo } from 'react';
import { FlatList, StyleSheet, Text, View } from 'react-native';
import { ActivityIndicator, Button, useTheme } from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { Job } from '../types/Job';
import { Truck } from '../types/Truck';
import JobService from '../services/jobService';
import TruckService from '../services/truckService';
import JobCard from './JobCard';
import { useFirestoreSubscription } from '../hooks/useFirestoreSubscription';

const ItemSeparator: React.FC = () => <View style={styles.separator} />;

interface JobListProps {
  limit?: number;
  showHeader?: boolean;
  embedded?: boolean;
  onSeeAll?: () => void;
  onJobPress?: (job: Job) => void;
}

const JobList: React.FC<JobListProps> = ({
  limit,
  showHeader = true,
  embedded = false,
  onSeeAll,
  onJobPress,
}) => {
  const theme = useTheme();

  const { data: allJobs, loading } = useFirestoreSubscription(
    (onUpdate, onError) => JobService.subscribeToAllJobs(onUpdate, onError),
    [] as Job[],
  );

  const { data: trucks } = useFirestoreSubscription(
    (onUpdate, onError) => TruckService.subscribeToAllTrucks(onUpdate, onError),
    [] as Truck[],
  );

  const truckMap = useMemo(() => {
    const map = new Map<string, string>();
    trucks.forEach(t => { if (t.id) map.set(t.id, t.plateNumber); });
    return map;
  }, [trucks]);

  const displayJobs = useMemo(() => {
    const completed = allJobs
      .filter(j => j.status === 'completed')
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    return limit ? completed.slice(0, limit) : completed;
  }, [allJobs, limit]);

  const renderRow = ({ item: job }: { item: Job }) => (
    <JobCard
      job={job}
      truckMap={truckMap}
      onPress={onJobPress}
    />
  );

  const renderHeader = () => {
    if (!showHeader) return null;
    return (
      <View style={styles.header}>
        <Text style={[styles.headerTitle, { color: theme.colors.onSurface }]}>
          Recent Jobs
        </Text>
        {onSeeAll && (
          <Button
            mode="text"
            onPress={onSeeAll}
            textColor={theme.colors.primary}
            testID="see-all-button"
          >
            See all
          </Button>
        )}
      </View>
    );
  };

  const renderEmpty = () => (
    <View style={styles.empty}>
      <MaterialCommunityIcons
        name="truck-fast-outline"
        size={48}
        color={theme.colors.outlineVariant}
      />
      <Text style={[styles.emptyText, { color: theme.colors.onSurfaceVariant }]}>
        No completed jobs yet
      </Text>
    </View>
  );

  if (loading && allJobs.length === 0) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (embedded) {
    return (
      <View style={styles.container}>
        {renderHeader()}
        {displayJobs.length === 0 ? (
          renderEmpty()
        ) : (
          <View style={styles.cardList}>
            {displayJobs.map(job => (
              <View key={job.id ?? ''}>{renderRow({ item: job })}</View>
            ))}
          </View>
        )}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={displayJobs}
        keyExtractor={item => item.id ?? ''}
        renderItem={renderRow}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={renderHeader}
        ListEmptyComponent={renderEmpty}
        contentContainerStyle={
          displayJobs.length === 0 ? styles.emptyContainer : styles.listContent
        }
        ItemSeparatorComponent={ItemSeparator}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 18,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingBottom: 24,
  },
  cardList: {
    paddingHorizontal: 16,
    gap: 10,
  },
  separator: {
    height: 10,
  },
  empty: {
    alignItems: 'center',
    paddingVertical: 32,
    gap: 12,
  },
  emptyText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
  },
  emptyContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  loading: {
    padding: 32,
    alignItems: 'center',
  },
});

export default JobList;
